import {
  MHOV_EXECUTE_THRESHOLD,
  MHOV_REWARD_EXPONENT,
  MHOV_REWARD_GROWTH_SCALE,
  MHOV_REWARD_MULTIPLIER_SOFTCAP_SLOPE,
  MHOV_REWARD_REFERENCE_HOV,
  MHOV_REWARD_REFERENCE_MULTIPLIER,
} from "@/features/hoverIdle/mHov/mHovLogic";
import { NHOV_BASE_HOV_THRESHOLD_VALUE } from "@/features/hoverIdle/nHov/nHovConstants";
import {
  NHOV_BRANCH_EXPERIENCE_GAIN_AT_REFERENCE,
  NHOV_BRANCH_EXPERIENCE_GAIN_EXPONENT,
} from "@/features/hoverIdle/nHov/nHovGrowthLogic";
import { NHOV_FIRST_HG_AMPLIFY_MIN_BRANCH_LEVEL } from "@/features/hoverIdle/nHov/nHovLogic";
import {
  NHOV_HG_AMPLIFY_CURRENT_GROWTH_BASE,
  NHOV_HG_AMPLIFY_EFFECTIVE_LEVEL_EXPONENT,
  NHOV_HG_AMPLIFY_VISIBLE_REFERENCE_MULTIPLIER,
} from "@/features/hoverIdle/nHov/hgAmplifyLogic";
import {
  NHOV_HG_COST_DIVISOR_FACTOR_PER_LEVEL,
  NHOV_HG_COST_DIVISOR_FACTOR_POW_BASE,
  NHOV_HG_DISCOUNT_EFFECTIVE_LEVEL_EXPONENT,
} from "@/features/hoverIdle/nHov/hgDiscountLogic";
import {
  HOV_CELL_RANK_SCALE_BASE,
  NHOV_HNG_SCALE_BASE_MULTIPLIER_PER_LEVEL,
  NHOV_HNG_SCALE_EFFECTIVE_LEVEL_EXPONENT,
} from "@/features/hoverIdle/nHov/hngScaleLogic";
import {
  HG_NHOV_EFFECTS_TO_HNG_EFFECTIVE_LEVEL_EXPONENT,
  HG_NHOV_EFFECTS_TO_HNG_REWARD_AT_REFERENCE,
} from "@/features/hoverIdle/nHov/hgNHovEffectsToHngLogic";
import type {
  HovResetBalance,
  HovResetBalanceOverrides,
} from "./hovResetBalanceTypes";

function freezeBalance(balance: HovResetBalance): HovResetBalance {
  Object.freeze(balance.mHov);
  Object.freeze(balance.nHov.branches.hgAmplify);
  Object.freeze(balance.nHov.branches.hgDiscount);
  Object.freeze(balance.nHov.branches.hngScale);
  Object.freeze(balance.nHov.branches.hgNHovEffectsToHng);
  Object.freeze(balance.nHov.branches);
  Object.freeze(balance.nHov);
  return Object.freeze(balance);
}

function createCurrentHovResetBalance(): HovResetBalance {
  return freezeBalance({
    mHov: {
      executeThreshold: MHOV_EXECUTE_THRESHOLD,
      rewardReferenceHov: MHOV_REWARD_REFERENCE_HOV.toNumber(),
      rewardReferenceMultiplier: MHOV_REWARD_REFERENCE_MULTIPLIER,
      rewardGrowthScale: MHOV_REWARD_GROWTH_SCALE,
      rewardExponent: MHOV_REWARD_EXPONENT,
      rewardSoftcapSlope: MHOV_REWARD_MULTIPLIER_SOFTCAP_SLOPE,
    },
    nHov: {
      baseHovThreshold: NHOV_BASE_HOV_THRESHOLD_VALUE,
      firstHgAmplifyMinBranchLevel: NHOV_FIRST_HG_AMPLIFY_MIN_BRANCH_LEVEL,
      experienceGainAtReference: NHOV_BRANCH_EXPERIENCE_GAIN_AT_REFERENCE,
      experienceGainExponent: NHOV_BRANCH_EXPERIENCE_GAIN_EXPONENT,
      branches: {
        hgAmplify: {
          visibleReferenceMultiplier:
            NHOV_HG_AMPLIFY_VISIBLE_REFERENCE_MULTIPLIER,
          growthBase: NHOV_HG_AMPLIFY_CURRENT_GROWTH_BASE,
          effectiveLevelExponent: NHOV_HG_AMPLIFY_EFFECTIVE_LEVEL_EXPONENT,
        },
        hgDiscount: {
          divisorPowerOffset: NHOV_HG_COST_DIVISOR_FACTOR_POW_BASE,
          factorPerLevel: NHOV_HG_COST_DIVISOR_FACTOR_PER_LEVEL,
          effectiveLevelExponent: NHOV_HG_DISCOUNT_EFFECTIVE_LEVEL_EXPONENT,
        },
        hngScale: {
          rankScaleBase: HOV_CELL_RANK_SCALE_BASE,
          multiplierPerLevel: NHOV_HNG_SCALE_BASE_MULTIPLIER_PER_LEVEL,
          effectiveLevelExponent: NHOV_HNG_SCALE_EFFECTIVE_LEVEL_EXPONENT,
        },
        hgNHovEffectsToHng: {
          rewardAtReference: HG_NHOV_EFFECTS_TO_HNG_REWARD_AT_REFERENCE,
          effectiveLevelExponent:
            HG_NHOV_EFFECTS_TO_HNG_EFFECTIVE_LEVEL_EXPONENT,
        },
      },
    },
  });
}

export const CURRENT_HOV_RESET_BALANCE = createCurrentHovResetBalance();

export function resolveHovResetBalance(
  overrides: HovResetBalanceOverrides = {},
): HovResetBalance {
  const current = CURRENT_HOV_RESET_BALANCE;
  const resolved: HovResetBalance = {
    mHov: { ...current.mHov, ...overrides.mHov },
    nHov: {
      ...current.nHov,
      ...overrides.nHov,
      branches: {
        hgAmplify: {
          ...current.nHov.branches.hgAmplify,
          ...overrides.nHov?.branches?.hgAmplify,
        },
        hgDiscount: {
          ...current.nHov.branches.hgDiscount,
          ...overrides.nHov?.branches?.hgDiscount,
        },
        hngScale: {
          ...current.nHov.branches.hngScale,
          ...overrides.nHov?.branches?.hngScale,
        },
        hgNHovEffectsToHng: {
          ...current.nHov.branches.hgNHovEffectsToHng,
          ...overrides.nHov?.branches?.hgNHovEffectsToHng,
        },
      },
    },
  };
  validateBalance(resolved);
  return freezeBalance(resolved);
}

function validateBalance(balance: HovResetBalance): void {
  assertPositive(balance.mHov.executeThreshold, "mHov.executeThreshold");
  assertPositive(balance.mHov.rewardReferenceHov, "mHov.rewardReferenceHov");
  assertPositive(
    balance.mHov.rewardReferenceMultiplier,
    "mHov.rewardReferenceMultiplier",
  );
  assertPositive(balance.mHov.rewardGrowthScale, "mHov.rewardGrowthScale");
  assertPositive(balance.mHov.rewardExponent, "mHov.rewardExponent");
  assertUnitIntervalExclusiveZero(
    balance.mHov.rewardSoftcapSlope,
    "mHov.rewardSoftcapSlope",
  );

  assertPositive(balance.nHov.baseHovThreshold, "nHov.baseHovThreshold");
  assertNonNegativeInteger(
    balance.nHov.firstHgAmplifyMinBranchLevel,
    "nHov.firstHgAmplifyMinBranchLevel",
  );
  assertPositive(
    balance.nHov.experienceGainAtReference,
    "nHov.experienceGainAtReference",
  );
  assertPositive(
    balance.nHov.experienceGainExponent,
    "nHov.experienceGainExponent",
  );

  assertPositive(
    balance.nHov.branches.hgAmplify.visibleReferenceMultiplier,
    "nHov.branches.hgAmplify.visibleReferenceMultiplier",
  );
  assertPositive(
    balance.nHov.branches.hgAmplify.growthBase,
    "nHov.branches.hgAmplify.growthBase",
  );
  assertPositive(
    balance.nHov.branches.hgAmplify.effectiveLevelExponent,
    "nHov.branches.hgAmplify.effectiveLevelExponent",
  );
  assertNonNegative(
    balance.nHov.branches.hgDiscount.divisorPowerOffset,
    "nHov.branches.hgDiscount.divisorPowerOffset",
  );
  assertPositive(
    balance.nHov.branches.hgDiscount.factorPerLevel,
    "nHov.branches.hgDiscount.factorPerLevel",
  );
  assertPositive(
    balance.nHov.branches.hgDiscount.effectiveLevelExponent,
    "nHov.branches.hgDiscount.effectiveLevelExponent",
  );
  assertPositive(
    balance.nHov.branches.hngScale.rankScaleBase,
    "nHov.branches.hngScale.rankScaleBase",
  );
  assertPositive(
    balance.nHov.branches.hngScale.multiplierPerLevel,
    "nHov.branches.hngScale.multiplierPerLevel",
  );
  assertPositive(
    balance.nHov.branches.hngScale.effectiveLevelExponent,
    "nHov.branches.hngScale.effectiveLevelExponent",
  );
  assertPositive(
    balance.nHov.branches.hgNHovEffectsToHng.rewardAtReference,
    "nHov.branches.hgNHovEffectsToHng.rewardAtReference",
  );
  assertPositive(
    balance.nHov.branches.hgNHovEffectsToHng.effectiveLevelExponent,
    "nHov.branches.hgNHovEffectsToHng.effectiveLevelExponent",
  );
}

function assertPositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be finite and > 0`);
  }
}

function assertNonNegative(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be finite and >= 0`);
  }
}

function assertNonNegativeInteger(value: number, name: string): void {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
}

function assertUnitIntervalExclusiveZero(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0 || value > 1) {
    throw new RangeError(`${name} must be finite, > 0, and <= 1`);
  }
}

export type {
  HovResetBalance,
  HovResetBalanceOverrides,
} from "./hovResetBalanceTypes";
